import React, { useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import {
	Card,
	ElementName,
	MetadataBlock,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { DEFAULT_MAX_SOURCES } from './constants';
import { createLinkClickHandlers } from './create-link-click-handlers';
import { LinkListErroredFallback } from './link-list-errored-fallback';
import { LinkListErroredItem } from './link-list-errored-item';
import { hasUrlResolved, markUrlResolved } from './resolved-url-cache';
import type { LinkListComponentProps, LinkListVariantProps } from './types';
import { useLinkListMarkErrored } from './use-link-list-mark-errored';

const styles = cssMap({
	root: {
		borderLeftColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	rootCompact: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	linkWrapperUnresolved: {
		minHeight: '56px',
	},
	linkWrapperUnresolvedCompact: {
		minHeight: '40px',
	},
	unresolvedSkeletonOverlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		zIndex: 1,
	},
	borderTop: {
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
	},
	borderTopRounded: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	borderBottomRounded: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
	loadingContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.150'),
	},
	loadingSkeletonRows: {
		flex: '1',
		minWidth: '0px',
	},
	footerAction: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		backgroundColor: 'transparent',
		border: 'none',
		cursor: 'pointer',
		textAlign: 'left',
		textDecoration: 'none',
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		// Match the row's outer left padding so the footer text lines up with the
		// left edge of the smartlink content.
		paddingLeft: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	// In compact density the row uses a smaller left padding, so match it here too.
	footerActionCompact: {
		paddingLeft: token('space.100'),
	},
});

const unboundedStyles = unboundedCssMap({
	unboundedTitleWrapper: {
		flex: 1,
		minWidth: '150px',
		overflow: 'hidden',
	},
	unboundedTitleBlock: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			gap: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-smart-element="LinkIcon"]': {
			paddingTop: token('space.100'),
			paddingRight: token('space.100'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.100'),
			borderRadius: token('radius.large'),
			backgroundColor: token('color.background.neutral'),
			marginTop: token('space.negative.050'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'a[data-smart-element="Title"]': {
			'&:hover': {
				textDecoration: 'none',
			},
			'&:focus': {
				textDecoration: 'none',
			},
			'&:active': {
				textDecoration: 'none',
			},
		},
	},
	unboundedMetadataWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="Priority"] > span[data-testid="smart-element-badge-label"]': {
			display: 'none',
		},
	},
	metadataStateSlot: {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'flex-end',
		marginLeft: token('space.150'),
	},
	metadataPrioritySlot: {
		flexShrink: 0,
		marginLeft: token('space.100'),
	},
});

const messages = defineMessages({
	link_list_expand: {
		id: 'smart_knowledge_card.link_list_compact_expand',
		defaultMessage: 'Show more',
		description:
			'Text shown on a button in the compact link list that expands the list to reveal additional hidden items.',
	},
	link_list_collapse: {
		id: 'smart_knowledge_card.link_list_compact_collapse',
		defaultMessage: 'Show less',
		description:
			'Text shown on a button in the compact link list that collapses the list back to its default shorter view.',
	},
	link_list_view_all: {
		id: 'smart_knowledge_card.link_list_compact_view_all',
		defaultMessage: 'View all',
		description:
			'Text shown on a link at the bottom of the compact link list that navigates the user to a full list view (used by consumers such as the user profile preview to surface every result on a dedicated page).',
	},
});

const LinkListCompactComponent = ({
	url,
	idx,
	borderTopRounded,
	borderBottomRounded,
	onError,
	onLinkClick,
	size = 'default',
	title,
}: LinkListComponentProps & { title?: string }) => {
	// When enabled, initialise from the module-level cache so a previously-resolved
	// URL does not flash its loading skeleton when this item is re-mounted (e.g.
	// while a Search AI answer or Rovo Extension chat streams in). See
	// `resolved-url-cache.ts`. When the gate is off, behave exactly as before.
	const isStreamingFlickerFixEnabled = fg('kd_link_list_streaming_flicker_fix');
	const isShowErroredLinksEnabled = fg('kd_link_list_show_errored_links');
	const [resolved, setResolved] = useState(() =>
		isStreamingFlickerFixEnabled ? hasUrlResolved(url) : false,
	);
	// Whether the Smart Card has reported an error for this URL.
	const [errored, setErrored] = useState(false);

	const handleResolve = () => {
		if (isStreamingFlickerFixEnabled) {
			markUrlResolved(url);
		}
		// A successful resolve always wins over a previous (possibly transient)
		// error, so clear the errored state.
		setErrored(false);
		setResolved(true);
	};

	const handleError = () => {
		// Mark the URL as both errored and resolved: `resolved` here means "the
		// Card has finished loading" . Treating the error as a terminal/resolved
		// state means the loading skeleton is shown for the entire resolving phase
		// and is only ever replaced by the errored layout once the Card is actually
		// done — never mid-resolve while content streams in.
		setErrored(true);
		setResolved(true);
		onError();
	};

	const { onClick, onAuxClick, onContextMenu } = createLinkClickHandlers(onLinkClick, url, idx);

	const isCompact = size === 'compact';

	// Show the custom errored layout in place only when Card has finished loading with an error.
	const showErroredLayout = isShowErroredLinksEnabled && errored && resolved;

	// The skeleton shows while the Card is still loading (not yet resolved/errored).
	const showSkeleton = !resolved;

	// Once the Card has finished loading with an error, render the errored row
	// content *instead of* the Card. The surrounding row container (border,
	// rounding, padding, hover, click telemetry) is identical to the resolved
	// row, so the errored row lines up perfectly with its neighbours.
	if (showErroredLayout) {
		return (
			<Box
				xcss={cx(
					styles.root,
					isCompact && styles.rootCompact,
					styles.borderTop,
					borderTopRounded && styles.borderTopRounded,
					borderBottomRounded && styles.borderBottomRounded,
				)}
				testId={`link-list-compact-errored-item-${idx}${borderTopRounded ? '-top-rounded' : ''}${
					borderBottomRounded ? '-bottom-rounded' : ''
				}`}
			>
				<LinkListErroredItem
					url={url}
					title={title}
					testId={`link-list-compact-errored-${idx}`}
					onClick={onClick}
					onAuxClick={onAuxClick}
					onContextMenu={onContextMenu}
				/>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.root,
				isCompact && styles.rootCompact,
				styles.borderTop,
				borderTopRounded && styles.borderTopRounded,
				borderBottomRounded && styles.borderBottomRounded,
				!resolved && styles.linkWrapperUnresolved,
				!resolved && isCompact && styles.linkWrapperUnresolvedCompact,
			)}
			testId={`link-list-compact-item-${idx}${borderTopRounded ? '-top-rounded' : ''}${
				borderBottomRounded ? '-bottom-rounded' : ''
			}`}
			onAuxClick={onAuxClick}
			onContextMenu={onContextMenu}
		>
			<Card
				appearance="block"
				url={url}
				onClick={onClick}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: isCompact ? SmartLinkSize.Small : SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onError={handleError}
				onResolve={handleResolve}
			>
				<Inline spread="space-between" alignBlock="center">
					{/* Left group: title + state, grows to fill available space */}
					<Inline alignBlock="center" xcss={unboundedStyles.unboundedTitleWrapper}>
						<Box xcss={unboundedStyles.unboundedTitleBlock}>
							<TitleBlock
								maxLines={1}
								showActionOnHover
								subtitle={[
									{ name: ElementName.Provider, hideIcon: true },
									{ name: ElementName.ModifiedOn },
								]}
								position={SmartLinkPosition.Center}
							/>
						</Box>
						<Box xcss={unboundedStyles.metadataStateSlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.State }]} />
						</Box>
					</Inline>
					<Inline alignBlock="center" xcss={unboundedStyles.unboundedMetadataWrapper}>
						<Box xcss={unboundedStyles.metadataPrioritySlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.Priority }]} />
						</Box>
					</Inline>
				</Inline>
			</Card>
			{showSkeleton && (
				<Box
					xcss={cx(styles.unresolvedSkeletonOverlay, styles.loadingContainer)}
					testId="unresolved-loading-skeleton"
				>
					<Inline space="space.100" alignBlock="center">
						<Skeleton width="16px" height="16px" borderRadius={token('radius.small')} />
						<Stack xcss={styles.loadingSkeletonRows} space="space.100">
							<Skeleton width="40%" height="12px" borderRadius={token('radius.small')} />
							<Skeleton width="70%" height="12px" borderRadius={token('radius.small')} />
						</Stack>
					</Inline>
				</Box>
			)}
		</Box>
	);
};

export const LinkListCompact = ({
	urls,
	titles,
	onLinkClick,
	size,
	maxSources = DEFAULT_MAX_SOURCES,
	expandedMaxSources,
	showExpand = true,
	viewAllUrl,
	onViewAllLinkClick,
	source,
}: LinkListVariantProps): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { erroredIndices, markErrored } = useLinkListMarkErrored();

	// Clamp `maxSources` to a sensible positive integer. The prop is typed as
	// `number`, so a caller can pass `0` (renders nothing until "Show more"
	// click) or a negative number (`slice(0, -1)` would silently drop the
	// last item). Falling back to `DEFAULT_MAX_SOURCES` keeps the API
	// forgiving without changing the documented happy-path behaviour.
	const safeMaxSources = maxSources >= 1 ? maxSources : DEFAULT_MAX_SOURCES;

	// When the show-errored-links gate is ON, every URL renders via
	// `LinkListCompactComponent`, preserving original order. A URL that the Smart
	// Link fails to resolve renders its errored layout *in place* (see the
	// component) rather than being dropped or moved, so the Smart Card stays
	// mounted and the loading skeleton is never preempted by a transient error
	// while content streams in.
	//
	// When the gate is OFF we preserve the original behaviour: errored URLs are
	// filtered out of the list entirely, and if every URL errors the plain-link
	// fallback below is shown instead.
	const isShowErroredLinksEnabled = fg('kd_link_list_show_errored_links');
	const visibleIndices = isShowErroredLinksEnabled
		? urls.map((_, i) => i)
		: urls.map((_, i) => i).filter((i) => !erroredIndices.has(i));

	const expandedCap =
		expandedMaxSources !== undefined && expandedMaxSources > safeMaxSources
			? expandedMaxSources
			: undefined;

	// Only show the expand/collapse button when showExpand is true and there are
	// more items than safeMaxSources. When showExpand is false all items up to
	// safeMaxSources are shown immediately with no toggle.
	const hasMore = showExpand && visibleIndices.length > safeMaxSources;

	const renderedIndices = isExpanded
		? expandedCap !== undefined
			? visibleIndices.slice(0, expandedCap)
			: visibleIndices
		: visibleIndices.slice(0, safeMaxSources);

	const firstVisibleIdx = renderedIndices[0] ?? -1;
	const lastVisibleIdx = renderedIndices[renderedIndices.length - 1] ?? -1;

	// In the edge case that every url has errored, rather than rendering nothing
	// (which looks broken given the surrounding context), fall back to rendering
	// each URL as a plain ADS link so the user still sees the links.
	//
	// This plain-link fallback is only used when the show-errored-links gate is
	// OFF. When the gate is ON we skip it and let every URL render via
	// `LinkListCompactComponent`, so an all-errored list shows the new in-place
	// errored layout per row instead.
	const allUrlsErrored = urls.length > 0 && erroredIndices.size === urls.length;

	if (allUrlsErrored && !isShowErroredLinksEnabled) {
		return (
			<LinkListErroredFallback
				urls={urls}
				onLinkClick={onLinkClick}
				testIdPrefix="link-list-compact"
				source={source}
			/>
		);
	}

	const showViewAll = !!viewAllUrl && (!hasMore || isExpanded);
	const showExpandToggle = hasMore && !showViewAll;
	const hasFooter = showExpandToggle || showViewAll;

	return (
		<Stack space="space.0">
			{renderedIndices.map((originalIdx) => (
				<LinkListCompactComponent
					key={`${urls[originalIdx]}-${originalIdx}`}
					url={urls[originalIdx]}
					title={titles?.[originalIdx]}
					idx={originalIdx}
					borderTopRounded={originalIdx === firstVisibleIdx}
					borderBottomRounded={originalIdx === lastVisibleIdx && !hasFooter}
					onError={() => markErrored(originalIdx)}
					onLinkClick={onLinkClick}
					size={size}
				/>
			))}

			{showExpandToggle && (
				<Pressable
					xcss={cx(
						styles.footerAction,
						size === 'compact' && styles.footerActionCompact,
						styles.borderTop,
						styles.borderBottomRounded,
					)}
					onClick={() => setIsExpanded(!isExpanded)}
					testId="link-list-compact-expand-toggle"
				>
					<Text color="color.text.subtlest">
						{isExpanded ? (
							<FormattedMessage {...messages.link_list_collapse} />
						) : (
							<FormattedMessage {...messages.link_list_expand} />
						)}
					</Text>
				</Pressable>
			)}

			{showViewAll && (
				<Anchor
					xcss={cx(
						styles.footerAction,
						size === 'compact' && styles.footerActionCompact,
						styles.borderTop,
						styles.borderBottomRounded,
					)}
					href={viewAllUrl}
					onClick={onViewAllLinkClick}
					testId="link-list-compact-view-all"
				>
					<Text color="color.text.subtlest">
						<FormattedMessage {...messages.link_list_view_all} />
					</Text>
				</Anchor>
			)}
		</Stack>
	);
};
